import { Control, ControlType, ControlWithinTree } from "@/types/controls";
import {
  AddPanelRules,
  BuilderElement,
  ElementSettings
} from "@/types/elements";
import { getControlAtPath } from "@/lib/controls/should-show-control";
import { configStore, documentStore, uiStore } from "@/store/store-accessor";
import { precompileTemplatesOfSingleElement } from "@/lib/twig/twig";
import Fuse from "fuse.js";
import {
  isElementInsideRestrictedAncestor,
  isElementRestrictedToBeChildOrDescendantOfSlug
} from "@/lib/tree/nesting";
import { TreeNode } from "@/types/tree";
import {
  getItemNameFromSlug,
  getItemNamespaceFromSlug
} from "@/lib/elements/studio/misc";
import { IconName } from "@/lib/icons/icons";
import { getElementOxySelectors } from "@/oxygen/classes/lib/classes";
import { COMPONENT_SLUG } from "@/lib/elements/constants";
import { breakdanceUndo } from "@/lib/undo/undo";

export function updateElementAndPrecompileItsTemplates(el: BuilderElement) {
  precompileTemplatesOfSingleElement(el);
  configStore.updateElement(el);
}

export function isControlTypeARepeater(controlType: ControlType | undefined) {
  return controlType === "repeater" || controlType === "inline_repeater";
}

export function doesControlTypeAcceptChildren(controlType: ControlType) {
  return isControlTypeARepeater(controlType) || controlType === "section";
}

export type ElementControlPathPrefix = "content" | "design" | "settings";

export type SeparatedPrefixFromElementControlPath = {
  prefix: ElementControlPathPrefix;
  pathWithoutPathPrefix: string;
};

export function getElementControlWithinTreeWithSplittedPathByElementControlPathWithPrefix(
  element: BuilderElement,
  elementControlPathWithPrefix: string
):
  | (ControlWithinTree & {
      splittedPath: SeparatedPrefixFromElementControlPath;
    })
  | undefined {
  const splittedPath = separatePathPrefixFromElementControlPath(
    elementControlPathWithPrefix
  );

  if (splittedPath) {
    const elementControls = getElementControlsListBasedOnPathPrefix(
      element,
      splittedPath.prefix
    );

    if (elementControls) {
      const controlAtPath = getControlAtPath(
        splittedPath.pathWithoutPathPrefix,
        elementControls
      );

      if (controlAtPath) {
        return {
          control: controlAtPath,
          controlsTree: elementControls,
          splittedPath
        };
      }
    }
  }

  return undefined;
}

export function getElementControlByElementControlPathWithPrefix(
  element: BuilderElement,
  elementControlPathWithPrefix: string
): Control | undefined {
  const controlWithinTreeWithSplittedPath =
    getElementControlWithinTreeWithSplittedPathByElementControlPathWithPrefix(
      element,
      elementControlPathWithPrefix
    );

  if (controlWithinTreeWithSplittedPath) {
    return controlWithinTreeWithSplittedPath.control;
  }

  return undefined;
}

/**
 * @param elementControlPathWithPrefix For example, "design.typography.color.some_breakpoint"
 */
export function separatePathPrefixFromElementControlPath(
  elementControlPathWithPrefix: string
): SeparatedPrefixFromElementControlPath | undefined {
  const splittedPath = elementControlPathWithPrefix.split(".");
  const maybePrefix = splittedPath.shift();

  // note: "settings" is used by global settings and element settings
  // "properties" by selectors
  if (
    maybePrefix &&
    ["content", "design", "settings", "properties"].includes(maybePrefix)
  ) {
    return {
      prefix: maybePrefix as ElementControlPathPrefix,
      pathWithoutPathPrefix: splittedPath.join(".")
    };
  }

  return undefined;
}

export function getElementControlsListBasedOnPathPrefix(
  element: BuilderElement,
  prefix: ElementControlPathPrefix
): Control[] | undefined {
  switch (prefix) {
    case "content":
      return element.controls.contentSections;
    case "design":
      return element.controls.designSections;
    case "settings":
      return element.controls.settingsSections;
    default:
      return undefined;
  }
}

export function propertyPathRule(newPath: string) {
  const onlyCharsAllowedInPath = /^[a-z._[\]]*$/g;

  if (newPath && !onlyCharsAllowedInPath.test(newPath.trim())) {
    return "Invalid path. Only lowercase letters, dot (.), underscore (_), and brackets ([]) allowed";
  }

  return true;
}

export function getFuzzySearchedElements(
  elements: BuilderElement[],
  search: string,
  extraKeys: string[] = []
) {
  // don't search if search is empty
  if (!search) return elements;

  const fuse = new Fuse(elements, {
    keys: [
      { name: "name", weight: 2 },
      "badge.label",
      "category",
      ...extraKeys
    ],
    threshold: 0.2
  });

  return fuse.search(search.trim()).map(r => r.item) as BuilderElement[];
}

export function isElementAvailable(element: BuilderElement) {
  if (
    element.nestingRule?.restrictedToBeADirectChildOf ||
    element.nestingRule?.restrictedToBeADescendantOf
  ) {
    if (!uiStore.activeElement) return false;

    if (
      isElementRestrictedToBeChildOrDescendantOfSlug(
        element,
        uiStore.activeElement.data.type
      )
    ) {
      return true;
    }

    return isElementInsideRestrictedAncestor(uiStore.activeElement, element);
  }

  return true;
}

export function isElementHidden(
  addPanelRules: AddPanelRules,
  settings: ElementSettings
) {
  if (settings && settings.requiredPlugins) {
    const availablePlugins = settings.requiredPlugins.filter(requiredPlugin =>
      configStore.availableWordpressPlugins.includes(requiredPlugin)
    );

    // if plugins are available, check the other rules
    if (availablePlugins.length !== settings.requiredPlugins.length) {
      return true;
    }
  }

  if (!addPanelRules) return false;

  if (addPanelRules.alwaysHide) return true;

  if (addPanelRules.allowedPostTypes) {
    return !addPanelRules.allowedPostTypes.includes(
      documentStore.document.documentMeta.postType
    );
  }

  return false;
}

export function getEmptyElement(): BuilderElement {
  return {
    name: "",
    className: false,
    uiIcon: false,
    slug: "",
    category: "",
    badge: false,
    htmlTag: {
      default: "",
      options: [],
      pathToControl: false
    },
    template: "",
    additionalClasses: false,
    attributes: false,
    cssTemplate: "",
    defaultCss: "",
    defaultProperties: false,
    defaultChildren: false,
    controls: {
      contentSections: [],
      designSections: [],
      settingsSections: []
    },
    dependencies: false,
    actions: false,
    nestingRule: { type: "section" },
    spacingBars: false,
    dynamicPropertyPaths: false,
    settings: false,
    addPanelRules: false,
    experimental: false,
    availableIn: ["breakdance"],
    order: 0,
    projectManagement: false,
    propertyPathsToWhitelistInFlatProps: false,
    propertyPathsToSsrElementWhenValueChanges: false
  };
}

//*** this path is also references in PHP ***
export function getNodeHtmlId(treeNode: TreeNode) {
  return String(treeNode.data.properties?.settings?.advanced?.id || "");
}

//*** The following three functions have a PHP equivalent ***

/**
 * Turns the uppercase of the namespace into "autogenerated-<suffix>"
 * "EssentialElements//Heading" becomes "autogenerated-ee-heading"
 */
function autogenerateClassNameFromElementSlug(slug: string) {
  if (slug === "root") return "root";

  const name = getItemNameFromSlug(slug).toLowerCase();

  const namespace = getItemNamespaceFromSlug(slug);
  const uppercaseLettersFromNamespace = namespace
    .replace(/[a-z]/g, "")
    .toLowerCase();

  const classSuffix =
    uppercaseLettersFromNamespace ||
    // just in case someone registered a namespace all lowercase like "mydumbnamespace'
    namespace.substr(0, 3);

  return `autogenerated-${classSuffix}-${name.toLowerCase()}`;
}

export function getClassNameForNode(node: TreeNode, documentId: number) {
  const element = configStore.element(node.data.type);
  return `${getBaseClassNameForBuilderElement(element)}-${documentId}-${
    node.id
  }`;
}

export function getBaseClassNameForBuilderElement(element: BuilderElement) {
  return (
    element.className || autogenerateClassNameFromElementSlug(element.slug)
  );
}

export function getElementIcon(node: TreeNode, fallbackToMaximizeIcon = false) {
  const builderElement = configStore.element(node.data.type);
  const builderMode = configStore.builderMode;

  const mapping: Record<string, IconName> = {
    h1: "H1Icon",
    h2: "H2Icon",
    h3: "H3Icon",
    h4: "H4Icon",
    h5: "H5Icon",
    h6: "H6Icon"
  };

  const htmlTag =
    node?.data?.properties?.settings?.advanced?.tag ||
    builderElement?.htmlTag?.default;

  const fallbackIcon = fallbackToMaximizeIcon
    ? "MaximizeIcon"
    : builderElement.uiIcon;

  if (builderMode === "oxygen") {
    return mapping[htmlTag] || builderElement.uiIcon;
  }

  return mapping[htmlTag] || fallbackIcon;
}

export function getElementName(node: TreeNode) {
  const selectors = getElementOxySelectors(node);
  const firstSelectorName = selectors[0]?.name;
  const friendlyName = node.data.properties?.meta?.friendlyName?.trim();
  const elementName = configStore.element(node.data.type).name;

  if (node.data.type === COMPONENT_SLUG) {
    const componentId =
      node.data.properties?.content?.content?.block?.componentId;

    const componentName = configStore.globalBlocks.find(
      b => b.id === componentId
    )?.label;

    if (componentName) {
      return `${componentName} (component)`;
    }
  }

  return friendlyName || firstSelectorName || elementName;
}

export function setElementHtmlTag(
  nodeId: TreeNode["id"],
  htmlTag: string,
  overwritePreviousTransactionWithKey?: string
) {
  breakdanceUndo.transaction(
    () => {
      documentStore.propertyChangedMutation({
        elementId: nodeId,
        path: "settings.advanced.tag",
        value: htmlTag,
        meta: {
          snapshotLabel: `Set html tag for element #${nodeId}`
        }
      });
    },
    `Set html tag for element #${nodeId}`,
    overwritePreviousTransactionWithKey
  );
}
